import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Auftragsbestand } from '../../services/erpSteering';
import { formatCents } from '../../services/freelancerLogic';
import { KartenRahmen } from './KartenRahmen';

// Auftragsbestand: je Projekt Auftragswert, fakturiert und offener Rest, dazu
// die Reichweite in Monaten (ERP/CRM-Plan Abschnitt 8).
//
// Duenne Darstellung: gerechnet ist alles in
// erpSteering.ts::berechneAuftragsbestand.

interface Props {
    zustand: { daten: Auftragsbestand | null; laedt: boolean; fehler: string | null };
    onOpenProject?: (projectId: string) => void;
}

const Kennzahl: React.FC<{ label: string; wert: string; hinweis?: string; ton?: 'normal' | 'warn' }> = ({
    label,
    wert,
    hinweis,
    ton = 'normal',
}) => (
    <div className={`rounded-xl border p-4 ${ton === 'warn' ? 'border-amber-200 bg-amber-50/50' : 'border-slate-200 bg-slate-50/60'}`}>
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{label}</p>
        <p className="mt-1 text-lg font-bold text-slate-800 tabular-nums">{wert}</p>
        {hinweis && <p className="mt-0.5 text-xs text-slate-500">{hinweis}</p>}
    </div>
);

export const AuftragsbestandCard: React.FC<Props> = ({ zustand, onOpenProject }) => {
    const d = zustand.daten;
    return (
        <KartenRahmen
            laedt={zustand.laedt}
            fehler={zustand.fehler}
            leer={!!d && d.zeilen.length === 0 && d.sammelrechnungen === 0}
            leerText="Kein Projekt mit Auftragswert oder Rechnung."
        >
            {d && (
                <div className="space-y-5">
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <Kennzahl label="Auftragswert" wert={formatCents(d.auftragswertSummeCents)} />
                        <Kennzahl label="Fakturiert" wert={formatCents(d.fakturiertSummeCents)} />
                        <Kennzahl label="Offener Bestand" wert={formatCents(d.offenSummeCents)} />
                        <Kennzahl
                            label="Reichweite"
                            wert={
                                d.reichweiteMonate === null
                                    ? '—'
                                    : `${d.reichweiteMonate.toLocaleString('de-DE', { maximumFractionDigits: 1 })} Monate`
                            }
                            hinweis={`Ø ${formatCents(d.monatsdurchschnittCents)} / Monat (12 Monate)`}
                        />
                    </div>

                    {(d.ohneAuftragswert > 0 || d.sammelrechnungen > 0) && (
                        <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                            <div className="space-y-1">
                                {d.ohneAuftragswert > 0 && (
                                    <p>
                                        <strong>{d.ohneAuftragswert}</strong> Projekte haben Rechnungen, aber keinen
                                        fixierten Auftragswert — sie zählen nicht in den offenen Bestand. Nachtragen
                                        unter „Auftragswerte“.
                                    </p>
                                )}
                                {d.sammelrechnungen > 0 && (
                                    <p>
                                        <strong>{d.sammelrechnungen}</strong> Sammelrechnungen über{' '}
                                        {formatCents(d.sammelrechnungenCents)} lassen sich keinem Projekt zuordnen
                                        (kein Hauptprojekt am Beleg) und sind hier nur nachrichtlich.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-widest text-slate-500">
                                    <th className="py-2 pr-3 font-bold">Projekt</th>
                                    <th className="py-2 pr-3 font-bold">Kunde</th>
                                    <th className="py-2 pr-3 text-right font-bold">Auftragswert</th>
                                    <th className="py-2 pr-3 text-right font-bold">Fakturiert</th>
                                    <th className="py-2 pr-3 text-right font-bold">Offen</th>
                                    <th className="py-2 pr-3 text-right font-bold">Fortschritt</th>
                                </tr>
                            </thead>
                            <tbody>
                                {d.zeilen.map((z) => (
                                    <tr key={z.projectId} className="border-b border-slate-100 hover:bg-slate-50/60">
                                        <td className="py-2 pr-3">
                                            <button
                                                type="button"
                                                className="text-left font-semibold text-slate-800 hover:text-blue-600 disabled:hover:text-slate-800"
                                                disabled={!onOpenProject}
                                                onClick={() => onOpenProject?.(z.projectId)}
                                            >
                                                {z.projektNummer || '—'}
                                            </button>
                                            <p className="truncate text-xs text-slate-500">{z.projektName || ''}</p>
                                        </td>
                                        <td className="py-2 pr-3 text-slate-600">{z.kundeName || '—'}</td>
                                        <td className="py-2 pr-3 text-right tabular-nums">
                                            {z.hatAuftragswert ? (
                                                formatCents(z.auftragswertCents)
                                            ) : (
                                                <span className="text-amber-700">nicht fixiert</span>
                                            )}
                                        </td>
                                        <td className="py-2 pr-3 text-right tabular-nums">{formatCents(z.fakturiertCents)}</td>
                                        <td
                                            className={`py-2 pr-3 text-right tabular-nums ${
                                                z.ueberfakturiert ? 'font-bold text-red-600' : ''
                                            }`}
                                        >
                                            {z.hatAuftragswert ? formatCents(z.offenCents) : '—'}
                                        </td>
                                        <td className="py-2 pr-3 text-right tabular-nums text-slate-600">
                                            {z.fortschrittProzent === null
                                                ? '—'
                                                : `${z.fortschrittProzent.toLocaleString('de-DE', { maximumFractionDigits: 0 })} %`}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </KartenRahmen>
    );
};
